import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AiOutlineReload } from "react-icons/ai";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import Modal from "react-modal";
import Navbar from "../../components/universal/Navbar";
import SearchBar from "../../components/universal/SearchBar";
import SquareCard from "../../components/universal/SquareCard";
import ReactECharts from "echarts-for-react";
import squareIcon1 from "../../assets/icons/squareCard/icon1.svg";
import userIcon from "../../assets/icons/userIcon.svg";
import {
  readConsentAnalyticsRequest,
  readConsentEntityAnalyticsRequest,
  readConsentEntityDetailsRequest,
} from "../../store/Agreements/agreementsActions";

const SUMMARY_CARDS = [
  { key: "acceptedCount", label: "Agreed", icon: squareIcon1 },
  { key: "rejectedCount", label: "Disagreed", icon: userIcon },
  { key: "pendingCount", label: "Pending", icon: squareIcon1 },
  { key: "totalCount", label: "Total", icon: userIcon },
];

const AgreementsAnalytics = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.agreements || {});
  const [entitySummary, setEntitySummary] = useState({});
  const [agreementTypeAnalytics, setAgreementTypeAnalytics] = useState({});
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activeCardKey, setActiveCardKey] = useState(null);
  const [activeEntityFilter, setActiveEntityFilter] = useState(null);
  const [activeAgreementFilter, setActiveAgreementFilter] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const detailRowsRef = useRef([]);
  const detailPendingRef = useRef(0);

  const fetchEntitySummary = async () => {
    const fetchDetails = (typeofentity, status) =>
      new Promise((resolve) => {
        dispatch(
          readConsentEntityDetailsRequest(
            { typeofentity, status, page: 1, limit: 200 },
            (res) => {
              const payload = res?.data || {};
              const rows = Array.isArray(payload)
                ? payload
                : Array.isArray(payload?.data)
                ? payload.data
                : [];
              resolve(rows);
            }
          )
        );
      });

    const entityTypes = ["1", "2", "3"];
    const statusByKey = {
      acceptedCount: "1",
      rejectedCount: "2",
      pendingCount: "0",
    };

    const map = {};
    for (const type of entityTypes) {
      const acceptedRows = await fetchDetails(type, statusByKey.acceptedCount);
      const rejectedRows = await fetchDetails(type, statusByKey.rejectedCount);
      const pendingRows = await fetchDetails(type, statusByKey.pendingCount);

      const acceptedCount = acceptedRows.length;
      const rejectedCount = rejectedRows.length;
      const pendingCount = pendingRows.length;
      const totalCount = acceptedCount + rejectedCount + pendingCount;

      map[type] = {
        data: [],
        summary: { acceptedCount, rejectedCount, pendingCount, totalCount },
      };
    }
    setEntitySummary(map);
  };

  const normalizeAnalytics = (response) => {
    const raw = response || {};
    const directRows = Array.isArray(raw?.data) ? raw.data : [];
    const nestedRows = Array.isArray(raw?.data?.data) ? raw.data.data : [];
    const data = directRows.length ? directRows : nestedRows;
    const summary = raw?.summary || raw?.data?.summary || null;
    return { data, summary };
  };

  const fetchAgreementTypeAnalytics = async () => {
    const fetchOne = (typeofentity) =>
      new Promise((resolve) => {
                dispatch(
          readConsentAnalyticsRequest({ typeofentity }, (res) => {
                        if (res?.type !== 1) {
                          }
            resolve(normalizeAnalytics(res?.data || {}));
          })
        );
      });

    const church = await fetchOne("1");
    const channel = await fetchOne("2");
    const ministry = await fetchOne("3");

    setAgreementTypeAnalytics({ "1": church, "2": channel, "3": ministry });
  };

  useEffect(() => {
    fetchEntitySummary();
    fetchAgreementTypeAnalytics();
  }, []);

  const computeSummaryFromRows = (rows = []) =>
    rows.reduce(
      (acc, row) => ({
        acceptedCount: acc.acceptedCount + Number(row?.acceptedCount || 0),
        rejectedCount: acc.rejectedCount + Number(row?.rejectedCount || 0),
        pendingCount: acc.pendingCount + Number(row?.pendingCount || 0),
        totalCount: acc.totalCount + Number(row?.totalCount || 0),
      }),
      { acceptedCount: 0, rejectedCount: 0, pendingCount: 0, totalCount: 0 }
    );

  const getEntitySummary = (entity) => {
    const rows = Array.isArray(entity?.data) ? entity.data : [];
    if (rows.length > 0) return computeSummaryFromRows(rows);
    return entity?.summary || {};
  };

  const entityChartData = useMemo(() => {
    const churchSummary = getEntitySummary(entitySummary?.["1"]);
    const channelSummary = getEntitySummary(entitySummary?.["2"]);
    const ministrySummary = getEntitySummary(entitySummary?.["3"]);
    const labels = ["Church", "Channel", "Ministry"];
    const totals = [
      Number(churchSummary?.totalCount || 0),
      Number(channelSummary?.totalCount || 0),
      Number(ministrySummary?.totalCount || 0),
    ];
    return { labels, totals };
  }, [entitySummary]);

  const combinedSummary = useMemo(() => {
    const church = getEntitySummary(entitySummary?.["1"]);
    const channel = getEntitySummary(entitySummary?.["2"]);
    const ministry = getEntitySummary(entitySummary?.["3"]);
    const add = (a, b) => Number(a || 0) + Number(b || 0);
    return {
      acceptedCount: add(add(church.acceptedCount, channel.acceptedCount), ministry.acceptedCount),
      rejectedCount: add(add(church.rejectedCount, channel.rejectedCount), ministry.rejectedCount),
      pendingCount: add(add(church.pendingCount, channel.pendingCount), ministry.pendingCount),
      totalCount: add(add(church.totalCount, channel.totalCount), ministry.totalCount),
    };
  }, [entitySummary]);

  const cardSummary = combinedSummary;
  const cardBreakdown = useMemo(() => {
    const church = getEntitySummary(entitySummary?.["1"]);
    const channel = getEntitySummary(entitySummary?.["2"]);
    const ministry = getEntitySummary(entitySummary?.["3"]);
    const makeRows = (key) => [
      { label: "Churches", count: Number(church?.[key] || 0) },
      { label: "Channels", count: Number(channel?.[key] || 0) },
      { label: "Ministry", count: Number(ministry?.[key] || 0) },
    ];
    return {
      acceptedCount: makeRows("acceptedCount"),
      rejectedCount: makeRows("rejectedCount"),
      pendingCount: makeRows("pendingCount"),
      totalCount: makeRows("totalCount"),
    };
  }, [entitySummary]);

  const entityPieOptions = {
    tooltip: {
      trigger: "item",
      formatter: (params) => `${params.name}: ${params.value}`,
    },
    series: [
      {
        name: "Total",
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: true,
        padAngle: 5,
        itemStyle: { borderRadius: 10 },
        label: { color: "#fff" },
        data: entityChartData.labels.map((label, index) => ({
          name: label,
          value: entityChartData.totals[index],
        })),
        color: ["#EFBC51", "#EFBC51", "#EFBC51"],
      },
    ],
  };

  const agreementTotals = useMemo(() => {
    const buckets = {};
    const appendRows = (rows = []) => {
      rows.forEach((row) => {
        const key = row?.agreementObjId || row?.agreementType || row?.agreementTitle || "unknown";
        if (!buckets[key]) {
          buckets[key] = {
            agreementObjId: row?.agreementObjId || key,
            agreementTitle: row?.agreementTitle || row?.agreementType || "Unknown",
            acceptedCount: 0,
            rejectedCount: 0,
            pendingCount: 0,
            totalCount: 0,
          };
        }
        if (row?.agreementTitle && !buckets[key].agreementTitle) {
          buckets[key].agreementTitle = row.agreementTitle;
        }
        buckets[key].acceptedCount += Number(row?.acceptedCount || 0);
        buckets[key].rejectedCount += Number(row?.rejectedCount || 0);
        buckets[key].pendingCount += Number(row?.pendingCount || 0);
        buckets[key].totalCount += Number(row?.totalCount || 0);
      });
    };

    appendRows(agreementTypeAnalytics?.["1"]?.data || []);
    appendRows(agreementTypeAnalytics?.["2"]?.data || []);
    appendRows(agreementTypeAnalytics?.["3"]?.data || []);

    return Object.values(buckets);
  }, [agreementTypeAnalytics]);

  const acceptedByAgreementType = useMemo(() => {
    const labels = agreementTotals.map(
      (row) => row?.agreementTitle || "Unknown"
    );
    const values = agreementTotals.map((row) => Number(row?.acceptedCount || 0));
    return { labels, values };
  }, [agreementTotals]);

  const agreementIdByLabel = useMemo(() => {
    const map = {};
    agreementTotals.forEach((row) => {
      const label = row?.agreementTitle || "Unknown";
      map[label] = row?.agreementObjId || row?.agreementType || label;
    });
    return map;
  }, [agreementTotals]);

  const acceptedLineOptions = {
    resposive: true,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
    xAxis: [
      {
        type: "category",
        data: acceptedByAgreementType.labels,
        axisLabel: { color: "#bfbfbf", rotate: 20 },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#333" } },
      },
    ],
    yAxis: [
      {
        type: "value",
        splitLine: { show: false, lineStyle: { type: "dashed" } },
        axisLabel: { color: "#bfbfbf" },
        axisLine: { lineStyle: { color: "#333" } },
      },
    ],
    series: [
      {
        name: "Accepted",
        type: "line",
        smooth: true,
        data: acceptedByAgreementType.values,
        lineStyle: { color: "#EFBC51", width: 3 },
        itemStyle: { color: "#EFBC51" },
        areaStyle: { color: "rgba(239,188,81,0.15)" },
      },
    ],
  };

  const activeCard = useMemo(
    () => SUMMARY_CARDS.find((card) => card.key === activeCardKey) || SUMMARY_CARDS[0],
    [activeCardKey]
  );

  const activeAgreementLabel = useMemo(() => {
    if (!activeAgreementFilter) return null;
    const match = agreementTotals.find(
      (row) => row?.agreementObjId === activeAgreementFilter
    );
    return match?.agreementTitle || activeAgreementFilter;
  }, [activeAgreementFilter, agreementTotals]);

  const statusByCardKey = {
    acceptedCount: "1",
    rejectedCount: "2",
    pendingCount: "0",
  };

  const entityTypeByLabel = {
    Church: "1",
    Channel: "2",
    Ministry: "3",
  };

  const fetchDetailRows = async ({ cardKey, entityLabel, agreementObjId }) => {
    const status = statusByCardKey[cardKey] ?? undefined;
    const entityTypes = entityLabel
      ? [entityTypeByLabel[entityLabel]].filter(Boolean)
      : ["1", "2", "3"];

    if (entityTypes.length === 0) {
      setDetailRows([]);
      setDetailsLoading(false);
      return;
    }

    const requestId = Date.now();
    detailRequestRef.current = requestId;
    detailRowsRef.current = [];
    detailPendingRef.current = entityTypes.length;
    setDetailRows([]);
    setDetailsLoading(true);

    // Safety: stop the spinner after 12s, but still allow late results to render.
    setTimeout(() => {
      if (detailRequestRef.current === requestId) {
        setDetailsLoading(false);
      }
    }, 12000);

    const normalizeRows = (res) => {
      const payload = res?.data || {};
      return Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
    };

    const handleResponse = (rows) => {
      rows.forEach((row) => {
        detailRowsRef.current.push({
          ...row,
          entityType:
            row?.typeofentity === "1"
              ? "Church"
              : row?.typeofentity === "2"
              ? "Channel"
              : row?.typeofentity === "3"
              ? "Ministry"
              : row?.typeofentity,
          entityName:
            row?.entityName ||
            row?.churchName ||
            row?.channelName ||
            row?.ministryName ||
            "Unknown",
        });
      });

      const merged = [...detailRowsRef.current].sort(
        (a, b) => new Date(b?.updatedAt) - new Date(a?.updatedAt)
      );
      setDetailRows(merged);
    };

    entityTypes.forEach((type) => {
      dispatch(
        readConsentEntityDetailsRequest(
          {
            typeofentity: type,
            status,
            agreementObjId,
            page: 1,
            limit: 200,
          },
          (res) => {
            if (detailRequestRef.current !== requestId) return;
            const rows = normalizeRows(res);
            if (rows.length > 0) {
              handleResponse(rows);
              setDetailsLoading(false);
            }
            detailPendingRef.current -= 1;
            if (detailPendingRef.current <= 0) {
              setDetailsLoading(false);
            }
          }
        )
      );
    });
  };

  const openDetailsModal = ({ cardKey, entityLabel, agreementObjId }) => {
    setActiveCardKey(cardKey);
    setActiveEntityFilter(entityLabel || null);
    setActiveAgreementFilter(agreementObjId || null);
    setDetailRows([]);
    setDetailsModalOpen(true);
    fetchDetailRows({ cardKey, entityLabel, agreementObjId });
  };

  const getEntityLabelForCard = (cardKey) => {
    if (!cardKey) return null;
    const church = getEntitySummary(entitySummary?.["1"]);
    const channel = getEntitySummary(entitySummary?.["2"]);
    const ministry = getEntitySummary(entitySummary?.["3"]);
    const counts = [
      { label: "Church", value: Number(church?.[cardKey] || 0) },
      { label: "Channel", value: Number(channel?.[cardKey] || 0) },
      { label: "Ministry", value: Number(ministry?.[cardKey] || 0) },
    ];
    counts.sort((a, b) => b.value - a.value);
    return counts[0]?.value > 0 ? counts[0].label : "Church";
  };

  const statusLabel = (status) => {
    if (status === "1") return "Agreed";
    if (status === "2") return "Disagreed";
    if (status === "0") return "Pending";
    return status || "";
  };

  const downloadDetailCsv = () => {
    const header = ["Name", "Email", "Mobile", "Agreement", "Status", "Entity"];
    const escape = (value) => {
      const str = String(value ?? "");
      if (str.includes('"') || str.includes(",") || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const lines = [
      header.join(","),
      ...detailRows.map((row) =>
        [
          escape(`${row.firstName || ""} ${row.lastName || ""}`.trim()),
          escape(row.email),
          escape(row.mobile),
          escape(row.agreementTitle),
          escape(statusLabel(row.status)),
          escape(row.entityType),
        ].join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `agreements-${activeCardKey || "acceptedCount"}-details.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="App bg-[#191919] h-fit min-h-screen flex p-4">
      <Navbar />
      <div className="flex-1 md:pl-32 md:pt-0 pt-20">
        <div className="flex flex-col gap-4 h-full">
          <SearchBar placeholder="Search" value={""} onChangeSearchBar={() => {}} />

          <div className="MAIN-CONTENT flex-1 flex flex-col h-full gap-4" id="reportContainer">
            <div className="flex w-full justify-between md:items-center md:flex-row flex-col gap-4 md:mb-0 mb-2">
              <div className="flex justify-between items-center flex-1">
                <p className="text-white text-3xl font-medium flex items-center gap-3">
                  Agreements Analytics
                  <AiOutlineReload
                    className={`cursor-pointer text-2xl text-yellowish ${
                      loading ? "animate-spin" : ""
                    }`}
                    onClick={() => {
                      fetchEntitySummary();
                      fetchAgreementTypeAnalytics();
                    }}
                  />
                </p>
              </div>
              <div className="flex gap-4">
                <Link to="/agreements/all">
                  <button className="text-[13px] text-greyish border px-4 py-2 border-greyish bg-blackish rounded-xl">
                    View All Agreements
                  </button>
                </Link>
                <Link to="/agreements/add">
                  <button className="text-[13px] text-yellowish border px-4 py-2 border-yellowish bg-blackish rounded-xl">
                    Add Agreement
                  </button>
                </Link>
              </div>
            </div>

            <div className="bg-blackish rounded-xl p-4">
              <div className="flex justify-end">
                <button
                  className="text-yellowish bg-blackish font-medium text-[12px] border border-yellowish rounded-xl py-2 px-6"
                  onClick={() => {
                    fetchEntitySummary();
                    fetchAgreementTypeAnalytics();
                  }}
                >
                  Refresh Analytics
                </button>
              </div>

              <div className="flex gap-4 flex-wrap mt-4">
                {SUMMARY_CARDS.map((card) => (
                  <SquareCard
                    key={card.key}
                    heading={`${card.label} Count`}
                    icon={card.icon}
                    iconStyle="h-11 w-11"
                    info={cardSummary?.[card.key] ?? 0}
                    trail={cardBreakdown?.[card.key] ?? ""}
                    percentage={""}
                    inlineInfo={true}
                    infoClassName={"text-2xl"}
                    breakdownRows={cardBreakdown?.[card.key]}
                    handleOnClick={() => {
                      openDetailsModal({
                        cardKey: card.key,
                        entityLabel: null,
                      });
                    }}
                  />
                ))}
              </div>

              <div className="mt-6">
                <p className="text-white text-sm font-semibold mb-3">Agreement Breakdown</p>
                <div className="flex gap-4 flex-wrap">
                  {agreementTotals.length === 0 && (
                    <p className="text-greyish text-sm">No analytics found.</p>
                  )}
                  {agreementTotals.map((row) => (
                    <div
                      key={row?.agreementObjId || row?.agreementTitle}
                      className="bg-blackishV2 rounded-2xl p-4 w-64"
                    >
                      <p className="text-white text-sm font-semibold mb-2">
                        {row?.agreementTitle || "Agreement"}
                      </p>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-greyish text-xs">Agreed</span>
                            <span className="text-yellowish text-xs font-semibold">
                              {row?.acceptedCount ?? 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-greyish text-xs">Disagreed</span>
                            <span className="text-yellowish text-xs font-semibold">
                              {row?.rejectedCount ?? 0}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-greyish text-xs">Pending</span>
                            <span className="text-yellowish text-xs font-semibold">
                              {row?.pendingCount ?? 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-greyish text-xs">Total</span>
                          <span className="text-yellowish text-2xl font-bold leading-none">
                            {row?.totalCount ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <p className="text-white text-sm font-semibold mb-3">
                    Agreements By Entity (Church / Channel / Ministry)
                  </p>
                  <div className="bg-blackishV2 rounded-2xl p-4">
                    <ReactECharts
                      className="py-4 bg-blackish mt-5"
                      option={entityPieOptions}
                      onEvents={{
                        click: (params) => {
                          openDetailsModal({
                            cardKey: "totalCount",
                            entityLabel: params?.name || null,
                          });
                        },
                      }}
                      style={{ height: 320 }}
                      notMerge={true}
                      lazyUpdate={true}
                      theme={"halloween"}
                      onChartReady={() => undefined}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <p className="text-white text-sm font-semibold mb-3">
                    Accepted Count By Agreement Type
                  </p>
                  <div className="bg-blackishV2 rounded-2xl p-4">
                    <ReactECharts
                      className="py-4 bg-blackish mt-5"
                      option={acceptedLineOptions}
                      onEvents={{
                        click: (params) => {
                          if (!params?.name) return;
                          openDetailsModal({
                            cardKey: "acceptedCount",
                            agreementObjId: agreementIdByLabel[params.name] || params.name,
                          });
                        },
                      }}
                      style={{ height: 320 }}
                      notMerge={true}
                      lazyUpdate={true}
                      theme={"halloween"}
                      onChartReady={() => undefined}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tooltip id="agreement-tooltip" className="border border-greyish border-opacity-20" />
      <Modal
        isOpen={detailsModalOpen}
        onRequestClose={() => setDetailsModalOpen(false)}
        contentLabel="Agreement Details"
        className="custom-modal absolute flex justify-center items-center z-[40] bg-black bg-opacity-50 h-screen w-screen py-6"
        ariaHideApp={false}
      >
        <div className="MODAL-BODY h-[95%] md:w-[70%] w-[92%] md:absolute rounded-2xl p-4 bg-blackishV2">
          <div className="w-full h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white text-lg font-semibold">
                {activeCard?.label} Details
                {activeEntityFilter ? ` - ${activeEntityFilter}` : ""}
                {activeAgreementLabel ? ` - ${activeAgreementLabel}` : ""}
              </p>
              <div className="flex items-center gap-3">
                <button
                  className="text-yellowish text-xs border border-yellowish px-3 py-2 rounded-lg"
                  onClick={downloadDetailCsv}
                >
                  Download CSV
                </button>
                <button
                  className="text-greyish text-sm"
                  onClick={() => setDetailsModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="rounded-xl bg-blackish flex-1 overflow-y-auto p-4">
              {detailsLoading ? (
                <p className="text-greyish text-sm">Loading details...</p>
              ) : detailRows.length === 0 ? (
                <p className="text-greyish text-sm">No details found.</p>
              ) : (
                <div className="w-full">
                  <div className="grid grid-cols-12 gap-3 text-greyish text-xs border-b border-greyishV2 pb-2">
                    <div className="col-span-2">Entity</div>
                    <div className="col-span-3">Name</div>
                    <div className="col-span-3">Email</div>
                    <div className="col-span-2">Mobile</div>
                    <div className="col-span-1">Agreement</div>
                    <div className="col-span-1 text-right">Status</div>
                  </div>
                  <div className="mt-2 flex flex-col gap-2">
                    {detailRows.map((row, index) => (
                      <div
                        key={`${row.userAuthId || row.entityObjId}-${index}`}
                        className="grid grid-cols-12 gap-3 text-white text-sm"
                      >
                        <div className="col-span-2">{row.entityType}</div>
                        <div className="col-span-3">
                          {row.firstName || ""} {row.lastName || ""}
                        </div>
                        <div className="col-span-3">{row.email || "—"}</div>
                        <div className="col-span-2">{row.mobile || "—"}</div>
                        <div
                          className="col-span-1 truncate"
                          data-tooltip-id="agreement-tooltip"
                          data-tooltip-content={row.agreementTitle}
                        >
                          {row.agreementTitle}
                        </div>
                        <div className="col-span-1 text-right text-yellowish font-semibold">
                          {statusLabel(row.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AgreementsAnalytics;
